import React, { forwardRef, useMemo, useState } from "react";
import MaterialTable from "material-table";
import AddBox from "@material-ui/icons/AddBox";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import HealthImage from "../../images/healthcondition.svg";
import useBDocTranslator from "../hooks/useBDocTranslator";
import EditIcon from "@material-ui/icons/Edit";
import {
  measurementPrompt,
  measurementTypes,
  clinicalRanges,
  statusColors,
} from "../../const/healthMeasurement";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { makeStyles } from "@material-ui/core";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { format, parseISO } from "date-fns";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
} from "@material-ui/core";
import TimelineIcon from "@material-ui/icons/Timeline";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import useUserState from "../hooks/useUserState";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles((theme) => ({
  EcgActions: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "1em",
  },
  actionIcon: {
    cursor: "pointer",
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
    padding: theme.spacing(2),
  },
  chartContainer: {
    width: "100%",
    height: "400px",
    padding: theme.spacing(2),
  },
  summaryBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
  },
  legendContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: "50%",
  },
  summaryGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  metricCard: {
    padding: theme.spacing(2),
    height: "100%",
    borderRadius: theme.spacing(1),
    position: "relative",
  },
  metricLabel: {
    textTransform: "uppercase",
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: "0.5px",
  },
  metricValue: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  metricUnit: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
  },
  metricSecondaryLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  statusChip: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    height: 24,
  },
}));

function Healthmeasurement(props) {
  const { t } = useBDocTranslator();
  const classes = useStyles();
  const [openChart, setOpenChart] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [userData] = useUserState();
  const handleOpenChart = async (measurementType, patientId) => {
    try {
      const [measurementsResponse, summaryResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/reports/health-measurements`,
          {
            params: {
              patientId,
              measurementType,
            },
            headers: {
              Authorization: `Bearer ${userData.jwt}`,
            },
          }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/reports/health-measurements/summary`,
          {
            params: {
              patientId,
              measurementType,
            },
            headers: {
              Authorization: `Bearer ${userData.jwt}`,
            },
          }
        ),
      ]);

      const chartResponseData = measurementsResponse.data.data;
      const summaryResponseData = summaryResponse.data.data;

      // BG measurements are stored as "X mg/dl - Y mmol/L" (two values).
      // The API treats this as a dual measurement and returns datasets labeled
      // "Systolic" and "Diastolic" (BP-style). Fix by keeping only the primary
      // (mg/dL) dataset and renaming it to "BG" so it matches clinicalRanges.
      if (measurementType === "BG" && chartResponseData?.datasets?.length > 0) {
        chartResponseData.datasets = [
          { ...chartResponseData.datasets[0], label: "BG" },
        ];
        if (summaryResponseData) {
          // Recalculate min/max/avg from the corrected mg/dL chart dataset so
          // that the Range summary card matches what the chart plots.
          const mgdlValues = chartResponseData.datasets[0].data.filter(
            (v) => v != null && !isNaN(Number(v))
          );
          if (mgdlValues.length > 0) {
            const nums = mgdlValues.map(Number);
            summaryResponseData.min_value = Math.min(...nums);
            summaryResponseData.max_value = Math.max(...nums);
            summaryResponseData.avg_value =
              nums.reduce((sum, v) => sum + v, 0) / nums.length;
          }
          summaryResponseData.avg_secondary = null;
          summaryResponseData.min_secondary = null;
          summaryResponseData.max_secondary = null;
        }
      }

      setChartData(chartResponseData);
      setSummaryData(summaryResponseData);
      setSelectedMeasurement(measurementType);
      setOpenChart(true);
    } catch (error) {
      console.error("Error fetching measurement data:", error);
    }
  };

  const dataFormated = useMemo(() => {
    if (!props.data) return [];
    return props.data
      .map((i) => {
        const mt = t(
          measurementTypes.find((mt) => mt.value === i.measurement_type)?.label
        );
        if (i.measurement_type === "ECG") {
          return {
            ...i,
            measurement_type: mt,
            result: (
              <span>
                {i.result.split("/*/")?.length
                  ? i.result.split("/*/")[0]
                  : "None"}
              </span>
            ),
            actions: (
              <div className={classes.EcgActions}>
                <CloudDownloadIcon
                  className={classes.actionIcon}
                  onClick={() => {
                    const parts = i.result.split("/*/");
                    const downloadUrl = parts[1]?.trim();
                    if (downloadUrl) {
                      window.open(downloadUrl, "_blank");
                    }
                  }}
                />
                {props.type === "pro" && (
                  <EditIcon
                    className={classes.actionIcon}
                    onClick={() => {
                      if (props.openEdit) props.openEdit(i);
                    }}
                  />
                )}
                <TimelineIcon
                  className={classes.actionIcon}
                  onClick={() =>
                    handleOpenChart(i.measurement_type, i.patient_id)
                  }
                />
              </div>
            ),
          };
        }
        return {
          ...i,
          measurement_type: mt,
          result: measurementPrompt[i.measurement_type]
            ? `${i.result} ${measurementPrompt[i.measurement_type]}`
            : i.result,
          actions: (
            <div className={classes.EcgActions}>
              {props.type === "pro" && (
                <EditIcon
                  className={classes.actionIcon}
                  onClick={() => {
                    if (props.openEdit) props.openEdit(i);
                  }}
                />
              )}
              <TimelineIcon
                className={classes.actionIcon}
                onClick={() =>
                  handleOpenChart(i.measurement_type, i.patient_id)
                }
              />
            </div>
          ),
        };
      })
      ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [props.data]);

  const formatChartData = (data) => {
    if (!data || !data.labels) return [];

    return data.labels.map((label, index) => {
      const result = {
        date: label,
      };

      data.datasets.forEach((dataset) => {
        result[dataset.label] = dataset.data[index];
      });

      return result;
    });
  };

  const getChartColors = () => ({
    Systolic: "#8884d8",
    Diastolic: "#82ca9d",
    BP: "#8884d8",
    SpO2: "#82ca9d",
    BG: "#ffc658",
    ECG: "#ff7300",
    BT: "#ff4444",
    HR: "#e91e63",
    RR: "#00acc1",
  });

  const getValueStatus = (value, datasetLabel, measurementType) => {
    const ranges = clinicalRanges[measurementType]?.[datasetLabel];
    if (!ranges) return "normal";
    if (value >= ranges.normal[0] && value <= ranges.normal[1]) return "normal";
    if (value >= ranges.alert[0] && value <= ranges.alert[1]) return "alert";
    if (ranges.alertLow && value >= ranges.alertLow[0] && value <= ranges.alertLow[1])
      return "alert";
    return "critical";
  };

  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    if (cx == null || cy == null) return null;
    const value = payload[dataKey];
    if (value == null) return null;
    const status = getValueStatus(value, dataKey, selectedMeasurement);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={statusColors[status]}
        stroke={statusColors[status]}
        strokeWidth={1}
      />
    );
  };

  const CustomActiveDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    if (cx == null || cy == null) return null;
    const value = payload[dataKey];
    if (value == null) return null;
    const status = getValueStatus(value, dataKey, selectedMeasurement);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={8}
        fill={statusColors[status]}
        stroke="#fff"
        strokeWidth={2}
      />
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          padding: "10px 14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 6 }}>
          {format(parseISO(label), "PPpp")}
        </Typography>
        {payload.map((entry) => {
          const status = getValueStatus(entry.value, entry.dataKey, selectedMeasurement);
          const ranges = clinicalRanges[selectedMeasurement]?.[entry.dataKey];
          const unit = ranges?.unit || measurementPrompt[selectedMeasurement] || "";
          return (
            <div
              key={entry.dataKey}
              style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: statusColors[status],
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {entry.dataKey}: {entry.value} {unit}
              </Typography>
            </div>
          );
        })}
      </div>
    );
  };

  const getReferenceLines = () => {
    const ranges = clinicalRanges[selectedMeasurement];
    if (!ranges) return [];
    const lines = [];
    Object.entries(ranges).forEach(([key, range]) => {
      const labelPrefix = Object.keys(ranges).length > 1 ? `${key} ` : "";
      lines.push({
        y: range.normal[1],
        label: `${labelPrefix}${t("Normal max")}`,
        key: `${key}-max`,
      });
      lines.push({
        y: range.normal[0],
        label: `${labelPrefix}${t("Normal min")}`,
        key: `${key}-min`,
      });
    });
    return lines;
  };

  const getYAxisDomain = () => {
    const ranges = clinicalRanges[selectedMeasurement];
    if (!ranges) return ["auto", "auto"];
    let minRef = Infinity;
    let maxRef = -Infinity;
    Object.values(ranges).forEach((range) => {
      if (range.alertLow) minRef = Math.min(minRef, range.alertLow[0]);
      else minRef = Math.min(minRef, range.normal[0]);
      if (range.alert) maxRef = Math.max(maxRef, range.alert[1]);
      else maxRef = Math.max(maxRef, range.normal[1]);
    });
    const padding = (maxRef - minRef) * 0.1;
    return [
      (dataMin) => Math.min(dataMin, minRef - padding),
      (dataMax) => Math.max(dataMax, maxRef + padding),
    ];
  };

  return (
    <div style={{ maxWidth: "100%" }}>
      <MaterialTable
        columns={[
          { title: "Id", field: "id", width: 60 },
          { title: t("Measurement"), field: "measurement_type", width: 120 },
          { title: t("Result"), field: "result", width: 150 },
          {
            title: t("Register date"),
            field: "created_at",
            type: "datetime",
            width: 140,
          },
          { title: t("Actions"), field: "actions", width: 100 },
        ]}
        data={dataFormated}
        title={t("Health Monitor Results")}
        icons={tableIcons}
        options={{
          headerStyle: {
            backgroundColor: "#01579b",
            color: "#FFF",
          },
          actionsColumnIndex: -1,
          tableLayout: "fixed",
          responsive: "stacked",
          overflowX: "auto",
          overflowY: "auto",
          maxBodyHeight: "400px",
          minBodyHeight: "200px",
          paging: true,
          pageSize: 5,
          pageSizeOptions: [5, 10, 20],
          search: true,
          showTitle: true,
          toolbar: true,
          emptyRowsWhenPaging: false,
          exportButton: false,
        }}
        localization={{
          body: {
            emptyDataSourceMessage: (
              <span>
                <img style={{ width: "15%" }} src={HealthImage} alt="" />
              </span>
            ),
          },
        }}
      />

      <Dialog
        open={openChart}
        onClose={() => setOpenChart(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            {t("Measurement History")} - {selectedMeasurement}
          </Typography>
          <IconButton onClick={() => setOpenChart(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className={classes.chartContainer}>
            {chartData && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={formatChartData(chartData)}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) =>
                      format(parseISO(value), "MM/dd HH:mm")
                    }
                  />
                  <YAxis domain={getYAxisDomain()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {getReferenceLines().map((line) => (
                    <ReferenceLine
                      key={line.key}
                      y={line.y}
                      stroke={statusColors.normal}
                      strokeDasharray="6 3"
                      strokeWidth={1.5}
                      label={{
                        value: line.label,
                        position: "right",
                        fill: statusColors.normal,
                        fontSize: 11,
                      }}
                    />
                  ))}
                  {chartData.datasets.map((dataset) => (
                    <Line
                      key={dataset.label}
                      type="monotone"
                      dataKey={dataset.label}
                      stroke={getChartColors()[dataset.label]}
                      dot={<CustomDot dataKey={dataset.label} />}
                      activeDot={<CustomActiveDot dataKey={dataset.label} />}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {summaryData && (() => {
            const isNumericMeasurement =
              !isNaN(Number(summaryData.min_value)) &&
              !isNaN(Number(summaryData.max_value)) &&
              String(summaryData.min_value).trim() !== "";
            const isDualMeasurement = summaryData.avg_secondary != null;
            const ranges = clinicalRanges[selectedMeasurement];
            const rangeKeys = ranges ? Object.keys(ranges) : [];

            const getUnit = (key) => {
              if (ranges && ranges[key]) return ranges[key].unit;
              return measurementPrompt[selectedMeasurement] || "";
            };

            const getAverageStatus = () => {
              if (!ranges) return null;
              if (isDualMeasurement) {
                const statuses = rangeKeys.map((key, idx) => {
                  const val = idx === 0
                    ? Number(summaryData.avg_value)
                    : Number(summaryData.avg_secondary);
                  return getValueStatus(val, key, selectedMeasurement);
                });
                if (statuses.includes("critical")) return "critical";
                if (statuses.includes("alert")) return "alert";
                return "normal";
              }
              const key = rangeKeys[0];
              return getValueStatus(Number(summaryData.avg_value), key, selectedMeasurement);
            };

            const avgStatus = isNumericMeasurement ? getAverageStatus() : null;
            const borderColor = avgStatus ? statusColors[avgStatus] : "#bdbdbd";
            const neutralBorder = "#bdbdbd";

            const statusLabels = {
              normal: t("Normal"),
              alert: t("Alert"),
              critical: t("Critical"),
            };

            const primaryUnit = isDualMeasurement ? getUnit(rangeKeys[0]) : getUnit(rangeKeys[0] || selectedMeasurement);
            const secondaryUnit = isDualMeasurement ? getUnit(rangeKeys[1]) : "";

            return (
              <Grid container spacing={2} className={classes.summaryGrid}>
                {/* Card 1 — Average */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    className={classes.metricCard}
                    style={{ borderLeft: `4px solid ${borderColor}` }}
                  >
                    <Typography className={classes.metricLabel}>
                      {t("Average")}
                    </Typography>
                    {!isNumericMeasurement ? (
                      <Box mt={0.5}>
                        <Typography variant="h5" className={classes.metricValue} style={{ color: "#9e9e9e" }}>
                          N/A
                        </Typography>
                        <Typography className={classes.metricSecondaryLabel}>
                          {t("Non-numeric data")}
                        </Typography>
                      </Box>
                    ) : isDualMeasurement ? (
                      <>
                        <Box display="flex" alignItems="baseline" mt={0.5}>
                          <Typography variant="h5" className={classes.metricValue}>
                            {Number(summaryData.avg_value).toFixed(1)}
                          </Typography>
                          <span className={classes.metricUnit}>{primaryUnit}</span>
                        </Box>
                        <Typography className={classes.metricSecondaryLabel}>
                          {ranges ? ranges[rangeKeys[0]]?.label : t("Systolic")}
                        </Typography>
                        <Box display="flex" alignItems="baseline" mt={0.5}>
                          <Typography variant="h5" className={classes.metricValue}>
                            {Number(summaryData.avg_secondary).toFixed(1)}
                          </Typography>
                          <span className={classes.metricUnit}>{secondaryUnit}</span>
                        </Box>
                        <Typography className={classes.metricSecondaryLabel}>
                          {ranges ? ranges[rangeKeys[1]]?.label : t("Diastolic")}
                        </Typography>
                      </>
                    ) : (
                      <Box display="flex" alignItems="baseline" mt={0.5}>
                        <Typography variant="h5" className={classes.metricValue}>
                          {Number(summaryData.avg_value).toFixed(1)}
                        </Typography>
                        <span className={classes.metricUnit}>{primaryUnit}</span>
                      </Box>
                    )}
                    {avgStatus && (
                      <Chip
                        size="small"
                        label={statusLabels[avgStatus]}
                        className={classes.statusChip}
                        style={{
                          backgroundColor: statusColors[avgStatus],
                          color: "#fff",
                        }}
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Card 2 — Range */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    className={classes.metricCard}
                    style={{ borderLeft: `4px solid ${neutralBorder}` }}
                  >
                    <Typography className={classes.metricLabel}>
                      {t("Range")}
                    </Typography>
                    {!isNumericMeasurement ? (
                      <Box mt={0.5}>
                        <Typography variant="h5" className={classes.metricValue} style={{ color: "#9e9e9e" }}>
                          N/A
                        </Typography>
                        <Typography className={classes.metricSecondaryLabel}>
                          {t("Non-numeric data")}
                        </Typography>
                      </Box>
                    ) : isDualMeasurement ? (
                      <>
                        <Box display="flex" alignItems="baseline" mt={0.5}>
                          <Typography variant="h5" className={classes.metricValue}>
                            {summaryData.min_value} – {summaryData.max_value}
                          </Typography>
                          <span className={classes.metricUnit}>{primaryUnit}</span>
                        </Box>
                        <Typography className={classes.metricSecondaryLabel}>
                          {ranges ? ranges[rangeKeys[0]]?.label : t("Systolic")}
                        </Typography>
                        <Box display="flex" alignItems="baseline" mt={0.5}>
                          <Typography variant="h5" className={classes.metricValue}>
                            {summaryData.min_secondary} – {summaryData.max_secondary}
                          </Typography>
                          <span className={classes.metricUnit}>{secondaryUnit}</span>
                        </Box>
                        <Typography className={classes.metricSecondaryLabel}>
                          {ranges ? ranges[rangeKeys[1]]?.label : t("Diastolic")}
                        </Typography>
                      </>
                    ) : (
                      <Box display="flex" alignItems="baseline" mt={0.5}>
                        <Typography variant="h5" className={classes.metricValue}>
                          {summaryData.min_value} – {summaryData.max_value}
                        </Typography>
                        <span className={classes.metricUnit}>{primaryUnit}</span>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Card 3 — Total + Last */}
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    className={classes.metricCard}
                    style={{ borderLeft: `4px solid ${neutralBorder}` }}
                  >
                    <Typography className={classes.metricLabel}>
                      {t("Measurements")}
                    </Typography>
                    <Box mt={0.5}>
                      <Typography variant="h5" className={classes.metricValue}>
                        {summaryData.total_measurements}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography className={classes.metricSecondaryLabel}>
                        {t("Last Measurement")}:
                      </Typography>
                      <Typography variant="body2">
                        {format(new Date(summaryData.last_measurement_date), "PPp")}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Healthmeasurement;
